import { Car, Bus, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Reveal } from "@/components/landing/Reveal";
import { useBooking } from "@/context/BookingContext";

const packages = [
  {
    key: "private",
    icon: Car,
    name: "Private Family Travel",
    tag: "The Private Luxury Journey",
    features: ["Dedicated Toyota Innova Crysta", "Fully custom pacing", "Selected 4★ stays", "24/7 Ground support"],
    price: "₹1.10L",
    unit: "per family group",
    featured: true,
  },
  {
    key: "micro-group",
    icon: Bus,
    name: "The Micro-Group Experience",
    tag: "The Curated Micro-Group",
    features: ["Luxury 10-seater Force Traveller", "Fixed seasonal dates", "Social yet highly relaxed pace", "Curated shared stays"],
    price: "₹24,999",
    unit: "per person",
    featured: false,
  },
];

export const Packaging = () => {
  const { openBooking } = useBooking();
  return (
    <section id="packaging" className="bg-[#F1EEE7] px-7 py-24 sm:px-12 lg:px-20 lg:py-32" data-testid="packaging-section">
      <div className="mx-auto max-w-5xl">
        <Reveal className="mb-16 text-center">
          <p className="eyebrow text-gold mb-5">Flexible Travel Packaging</p>
          <h2 className="font-display text-4xl leading-tight text-ink sm:text-5xl lg:text-6xl">
            Two ways to <span className="italic text-gold">travel deeply.</span>
          </h2>
        </Reveal>

        <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
          {packages.map((p, i) => (
            <Reveal key={p.key} delay={i * 0.12} className="h-full">
              <div
                className={`flex h-full flex-col p-8 lg:p-10 ${p.featured ? "border border-gold/50 bg-ink text-white shadow-2xl" : "border border-black/10 bg-white text-ink"}`}
                data-testid={`package-card-${p.key}`}
              >
                <p.icon className={`h-8 w-8 ${p.featured ? "text-gold" : "text-ink"}`} strokeWidth={1.5} />
                <p className={`eyebrow mt-6 ${p.featured ? "text-gold" : "text-black/40"}`}>{p.tag}</p>
                <h3 className="mt-2 font-display text-2xl">{p.name}</h3>

                <ul className={`mt-7 flex-1 space-y-3 text-sm ${p.featured ? "text-white/70" : "text-black/60"}`}>
                  {p.features.map((f) => (
                    <li key={f} className="flex items-center gap-3">
                      <span className={`h-1.5 w-1.5 rounded-full ${p.featured ? "bg-gold" : "bg-ink"}`} />
                      {f}
                    </li>
                  ))}
                </ul>

                <div className="mt-8 border-t pt-6" style={{ borderColor: p.featured ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.1)" }}>
                  <p className={`text-xs uppercase tracking-wider ${p.featured ? "text-white/45" : "text-black/40"}`}>Starting from</p>
                  <p className="mt-1 font-display text-4xl">
                    {p.price}
                    <span className={`ml-2 text-sm font-sans ${p.featured ? "text-white/50" : "text-black/45"}`}>{p.unit}</span>
                  </p>
                  <p className={`mt-1 text-xs ${p.featured ? "text-white/40" : "text-black/40"}`}>Excluding flights</p>
                </div>

                <Button
                  data-testid={`book-${p.key}-btn`}
                  onClick={() => openBooking(p.name)}
                  className={`group mt-7 h-12 w-full rounded-none text-sm font-bold uppercase tracking-[0.1em] transition-all duration-300 hover:-translate-y-0.5 ${p.featured ? "bg-gold text-ink hover:bg-gold-hover" : "bg-ink text-white hover:bg-ink/90"}`}
                >
                  Plan My Trip
                  <ArrowRight className="ml-1 h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                </Button>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};
